import { z } from 'zod';
import { assignmentSchema } from './issuance.js';
/**
 * Missions (REQUIREMENTS §2.4 `missions`, §2.5 "Missions"): gear drawn for a
 * specific operation, tracked as a set with an expected return. The mission
 * itself holds nothing personal — a name, dates, and the units it concerns.
 */
export const missionStatusSchema = z.enum(['planned', 'active', 'closed']);
export const missionSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullable(),
    startAt: z.string().nullable(),
    /** The expected return. Gear still out after it is late (§2.6 alerts). */
    endAt: z.string().nullable(),
    status: missionStatusSchema,
    createdBy: z.string().min(1),
    createdAt: z.string().nullable(),
    /** Platoons or teams the mission concerns; decides who may see and draw on it. */
    unitIds: z.array(z.string().min(1)),
});
/**
 * POST /api/missions — admin, csm, or a platoon commander for a
 * platoon they command (§2.6 lists "Mission management" as a storeroom
 * screen; the platoon commander draws for their own platoon's exercise).
 */
export const createMissionRequestSchema = z.object({
    name: z.string().trim().min(2).max(120),
    description: z.string().trim().max(1000).nullable().default(null),
    startAt: z.string().datetime().nullable().default(null),
    endAt: z.string().datetime().nullable().default(null),
    status: missionStatusSchema.default('planned'),
    unitIds: z.array(z.string().min(1)).min(1).max(50),
});
/** PATCH /api/missions/:id — every field optional; closing is a status change. */
export const updateMissionRequestSchema = createMissionRequestSchema.partial();
export const missionResponseSchema = z.object({ mission: missionSchema });
/**
 * GET /api/missions — every mission in the caller's reach, with the counts
 * the list screen shows: how many assignments were drawn, how many are back,
 * how many are late.
 */
export const missionSummarySchema = missionSchema.extend({
    drawn: z.number().int().min(0),
    returned: z.number().int().min(0),
    overdue: z.number().int().min(0),
});
export const missionsResponseSchema = z.object({ missions: z.array(missionSummarySchema) });
/**
 * GET /api/missions/:id — what was drawn, who holds it, what is back, what is
 * late (§2.6 "Mission view"). `overdue` is computed by the server so every
 * screen agrees on what late means: an open assignment past its due date, or
 * past the mission's end when the assignment has no due date of its own.
 */
export const missionAssignmentSchema = assignmentSchema.extend({ overdue: z.boolean() });
export const missionDetailResponseSchema = z.object({
    mission: missionSchema,
    assignments: z.array(missionAssignmentSchema),
    soldiers: z.array(z.object({ uid: z.string(), name: z.string(), unitName: z.string().nullable() })),
    /**
     * Whether the caller may edit this mission — every unit it names is in
     * their reach (story 38). Decided by the server because a platoon leader
     * can *view* a mission that also covers another platoon, and roles alone
     * cannot tell the page whether to offer the button.
     */
    canManage: z.boolean(),
});
//# sourceMappingURL=missions.js.map